/**
 * WeatherSection Component
 * Weather data entry for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, Cloud } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import type { WeatherCondition, PrecipitationType } from '../../../types/dailyLog';
import { Dropdown, NumberInput, TextArea } from '../../forms';

// Weather condition options with icons
const WEATHER_CONDITION_OPTIONS = [
  { value: 'sunny', label: 'Sunny' },
  { value: 'partly_cloudy', label: 'Partly Cloudy' },
  { value: 'cloudy', label: 'Cloudy' },
  { value: 'rain', label: 'Rain' },
  { value: 'snow', label: 'Snow' },
  { value: 'fog', label: 'Fog' },
  { value: 'windy', label: 'Windy' },
  { value: 'storm', label: 'Storm' },
];

const TEMPERATURE_UNIT_OPTIONS = [
  { value: 'F', label: 'Fahrenheit (°F)' },
  { value: 'C', label: 'Celsius (°C)' },
];

const PRECIPITATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'rain', label: 'Rain' },
  { value: 'snow', label: 'Snow' },
  { value: 'sleet', label: 'Sleet' },
  { value: 'hail', label: 'Hail' },
];

// Form data interface matching daily log weather entry
export interface WeatherFormData {
  conditions: WeatherCondition;
  temperatureHigh: number;
  temperatureLow: number;
  temperatureUnit: 'F' | 'C';
  humidity?: number;
  windSpeed?: number;
  precipitation?: number;
  precipitationType: PrecipitationType;
  delayHours: number;
  notes?: string;
}

interface WeatherSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const WeatherSection: React.FC<WeatherSectionProps> = ({
  control,
  errors,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const weatherErrors = errors.weather as FieldErrors<WeatherFormData> | undefined;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Cloud className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Weather Conditions</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Weather Conditions & Temperature */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Conditions */}
            <Controller
              name="weather.conditions"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Conditions"
                  options={WEATHER_CONDITION_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.conditions?.message}
                  disabled={disabled}
                  required
                />
              )}
            />

            {/* Temperature High */}
            <Controller
              name="weather.temperatureHigh"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="High Temperature"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.temperatureHigh?.message}
                  disabled={disabled}
                  min={-50}
                  max={150}
                  required
                />
              )}
            />

            {/* Temperature Low */}
            <Controller
              name="weather.temperatureLow"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Low Temperature"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.temperatureLow?.message}
                  disabled={disabled}
                  min={-50}
                  max={150}
                  required
                />
              )}
            />
          </div>

          {/* Temperature Unit & Humidity & Wind */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Temperature Unit */}
            <Controller
              name="weather.temperatureUnit"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Unit"
                  options={TEMPERATURE_UNIT_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.temperatureUnit?.message}
                  disabled={disabled}
                />
              )}
            />

            {/* Humidity */}
            <Controller
              name="weather.humidity"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Humidity (%)"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.humidity?.message}
                  disabled={disabled}
                  min={0}
                  max={100}
                  placeholder="0-100"
                />
              )}
            />

            {/* Wind Speed */}
            <Controller
              name="weather.windSpeed"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Wind Speed (mph)"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.windSpeed?.message}
                  disabled={disabled}
                  min={0}
                  max={200}
                  placeholder="mph"
                />
              )}
            />

            {/* Delay Hours */}
            <Controller
              name="weather.delayHours"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Weather Delay (hours)"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.delayHours?.message}
                  disabled={disabled}
                  min={0}
                  max={24}
                  step={0.25}
                />
              )}
            />
          </div>

          {/* Precipitation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Precipitation Type */}
            <Controller
              name="weather.precipitationType"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Precipitation Type"
                  options={PRECIPITATION_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.precipitationType?.message}
                  disabled={disabled}
                />
              )}
            />

            {/* Precipitation Amount */}
            <Controller
              name="weather.precipitation"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Precipitation Amount (inches)"
                  value={field.value}
                  onChange={field.onChange}
                  error={weatherErrors?.precipitation?.message}
                  disabled={disabled}
                  min={0}
                  step={0.1}
                  placeholder="inches"
                />
              )}
            />
          </div>

          {/* Notes */}
          <Controller
            name="weather.notes"
            control={control}
            render={({ field }) => (
              <TextArea
                label="Weather Notes"
                value={field.value || ''}
                onChange={field.onChange}
                error={weatherErrors?.notes?.message}
                disabled={disabled}
                rows={2}
                placeholder="Additional weather observations..."
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default WeatherSection;
